import { useState } from "react";
import { Box, Typography, Colors } from "@analyticsMD/quil";
import { css } from "@emotion/css";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import Button from "@mui/material/Button";
import MalnutritionHeader from "./malnutrition-header";
import WhyFlaggedSection from "./why-flagged";
import SupportingDataSection from "./supporting-data";

import type { FC, ReactNode } from "react";
import type {
  MalnutritionPatientSummary,
  MalnutritionCriterion,
  MalnutritionStatus,
  PatientWeightHistory,
} from "../../types/Malnutrition";

const FEEDBACK_PURPLE_TEXT = "#6A3FA0";

interface StatusBannerTheme {
  label: string;
  bg: string;
  fg: string;
  icon: "error" | "check";
}

const STATUS_BANNER: Record<MalnutritionStatus, StatusBannerTheme> = {
  high_risk: { label: "High Risk", bg: "#FCE9E8", fg: "#C62B2B", icon: "error" },
  medium_risk: {
    label: "Medium Risk",
    bg: "#FDF1E0",
    fg: "#B96B00",
    icon: "error",
  },
  protocol: { label: "Protocol", bg: "#E5EBFB", fg: "#2D4FBE", icon: "error" },
  following: {
    label: "Following",
    bg: "#E5F2EA",
    fg: "#2E7D32",
    icon: "check",
  },
  consult: { label: "Consult", bg: "#E5F2EA", fg: "#2E7D32", icon: "check" },
};

const styles = {
  pageBg: css`
    background: #f1edf5;
    min-height: 100vh;
  `,
  feedbackCard: css`
    background: #ffffff;
    margin: 0.75rem 0.75rem 0;
    padding: 0.75rem 0.875rem;
    border-radius: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  `,
  feedbackHeader: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
  feedbackTitleRow: css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
  `,
  feedbackIcon: css`
    background: ${FEEDBACK_PURPLE_TEXT};
    color: ${Colors.white};
    border-radius: 0.25rem;
    padding: 0.125rem;
  `,
  feedbackPromptRow: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  `,
};

const sxStyles = {
  feedbackTitle: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: FEEDBACK_PURPLE_TEXT,
  },
  feedbackPrompt: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: Colors.grey_80,
  },
  feedbackButton: {
    textTransform: "none",
    borderRadius: "1.25rem",
    minWidth: "3rem",
    height: "1.875rem",
    fontWeight: 600,
    color: Colors.grey_80,
    borderColor: Colors.grey_40,
  },
};

interface StatusBannerProps {
  status: MalnutritionStatus;
}

const StatusBanner: FC<StatusBannerProps> = ({ status }) => {
  const theme = STATUS_BANNER[status];
  const Icon = theme.icon === "check" ? CheckCircleIcon : ErrorIcon;
  return (
    <Box
      role="status"
      aria-label={`Malnutrition status: ${theme.label}`}
      sx={{
        background: theme.bg,
        color: theme.fg,
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        padding: "0.5rem 1rem",
        fontWeight: 700,
        fontSize: "0.875rem",
      }}
    >
      <Icon sx={{ fontSize: "1.125rem" }} />
      <span>{theme.label}</span>
    </Box>
  );
};

const FeedbackCard: FC = () => {
  const [submitted, setSubmitted] = useState<"yes" | "no" | null>(null);

  const handleVote = (vote: "yes" | "no") => () => {
    setSubmitted(vote);
    // Persistence is wired in a follow-up; the scaffold acknowledges locally.
  };

  return (
    <Box className={styles.feedbackCard}>
      <Box className={styles.feedbackHeader}>
        <Box className={styles.feedbackTitleRow}>
          <PlaylistAddCheckIcon className={styles.feedbackIcon} sx={{ fontSize: "1rem" }} />
          <Typography sx={sxStyles.feedbackTitle}>Help us improve</Typography>
        </Box>
      </Box>
      <Box className={styles.feedbackPromptRow}>
        <Typography sx={sxStyles.feedbackPrompt}>
          Good candidate for assessment?
        </Typography>
        <Box sx={{ display: "flex", gap: "0.5rem" }}>
          <Button
            variant={submitted === "yes" ? "contained" : "outlined"}
            size="small"
            onClick={handleVote("yes")}
            sx={sxStyles.feedbackButton}
            disabled={submitted !== null}
          >
            Yes
          </Button>
          <Button
            variant={submitted === "no" ? "contained" : "outlined"}
            size="small"
            onClick={handleVote("no")}
            sx={sxStyles.feedbackButton}
            disabled={submitted !== null}
          >
            No
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

interface MalnutritionSectionsProps {
  patient: MalnutritionPatientSummary;
  status: MalnutritionStatus;
  criteria: MalnutritionCriterion[];
  weights: PatientWeightHistory;
  onClose?: () => void;
  onExpand?: () => void;
  closeButtonCmpt?: ReactNode;
}

const MalnutritionSections: FC<MalnutritionSectionsProps> = ({
  patient,
  status,
  criteria,
  weights,
  onClose,
  onExpand,
  closeButtonCmpt,
}) => (
  <Box className={styles.pageBg}>
    <MalnutritionHeader
      patient={patient}
      onClose={onClose}
      onExpand={onExpand}
      closeButtonCmpt={closeButtonCmpt}
    />
    <StatusBanner status={status} />
    <FeedbackCard />
    <WhyFlaggedSection criteria={criteria} status={status} />
    <SupportingDataSection weights={weights} />
  </Box>
);

export default MalnutritionSections;
